/**
 * `printer.ts` — alamat & profil printer (K6, keputusan terbuka 🔶#3).
 *
 * ### ⚠️ K6 harus dipersempit: *"printer = data, bukan state OS"* berlaku PENUH hanya untuk Ethernet
 *
 * | Transport | Alamat | K6 |
 * |---|---|---|
 * | `net` | node jaringan `host:9100` — terjangkau dari mesin mana pun di LAN | ✅ murni data |
 * | `usb` | menempel pada **satu mesin fisik** (`vendorId:productId`) | ⚠️ data, tapi hanya bermakna **relatif terhadap `device_code`** |
 * | `bt` | menempel pada satu mesin **dan pairing-nya state OS** | ⛔ pairing TAK BISA jadi data — ia prasyarat, dan diakui begitu |
 *
 * ⇒ Konsekuensi arsitektur, bukan detail: dengan `net`, `outlet-server` bisa mencetak sendiri.
 * Dengan `usb`/`bt`, **`device-agent` menjadi WAJIB** (K4) dan peramban tak bisa mencetak langsung.
 * WebUSB/Web Bluetooth menuntut gestur pengguna tiap sesi ⇒ tak layak untuk kasir.
 *
 * @module
 */
/** Cara byte sampai ke printer. */
export const PRINTER_TRANSPORTS = Object.freeze(['net', 'usb', 'bt']);
const TRANSPORT_SET = new Set(PRINTER_TRANSPORTS);
/** Guard {@link PrinterTransport}. */
export function isPrinterTransport(v) {
    return typeof v === 'string' && TRANSPORT_SET.has(v);
}
/** Lebar kertas — menentukan jumlah kolom, jadi ia bagian dari kebenaran struk, bukan gaya. */
export const PAPER_WIDTHS = Object.freeze([58, 80]);
/** Kolom karakter per lebar kertas pada font A (12×24). Dipakai renderer untuk merapikan kolom. */
export const COLUMNS = Object.freeze({ 58: 32, 80: 48 });
/**
 * ⭐ **Ukuran huruf isi struk** — DATA dari portal, bukan dipahat renderer (Guardrail #4).
 *
 * ⛔ Sengaja DUA nilai, bukan angka bebas: ESC/POS cuma punya dua font terpasang (A 12×24 dan
 * B 9×17). "Ukuran 11" tak punya arti di printer termal, dan menawarkannya di layar berarti
 * setelan yang berbohong kepada yang menyetelnya.
 */
export const RECEIPT_FONTS = Object.freeze(['normal', 'small']);
/** Ukuran NAMA OUTLET di kepala struk. `large` = dobel lebar & tinggi. */
export const RECEIPT_SCALES = Object.freeze(['normal', 'large']);
/**
 * ⭐⭐ Kolom per lebar kertas **menurut FONT**.
 *
 * ⚠️ Ini bukan tabel gaya. Seluruh perataan kolom di renderer dihitung dari angka ini; menukar
 * font tanpa menukar jumlah kolom menghasilkan struk yang kolom kanannya meleset — dan melesetnya
 * baru terlihat di kertas, bukan di pratinjau mana pun.
 *
 * Font B 9 titik lebar: 384/9 = 42 (58 mm) · 576/9 = 64 (80 mm).
 */
export const COLUMNS_BY_FONT = Object.freeze({
    normal: COLUMNS,
    small: Object.freeze({ 58: 42, 80: 64 }),
});
/**
 * ⭐ **Lebar cetak dalam TITIK** — yang dipakai gambar raster (logo), bukan kolom karakter.
 *
 * ⚠️ Angkanya bukan `mm × dpi`: kertas 58 mm mencetak 48 mm (384 titik) dan 80 mm mencetak 72 mm
 * (576 titik) — sisanya margin fisik mekanisme. Memakai lebar kertas apa adanya menghasilkan logo
 * yang terpotong di kanan pada SEMUA printer, dan gejalanya terlihat seperti gambarnya yang salah.
 */
export const DOTS = Object.freeze({ 58: 384, 80: 576 });
/*
 * ⚠️ Base64 ditulis TANGAN, bukan `btoa`/`Buffer`.
 *
 * `contract` wajib NOL dependensi runtime dan dipakai di peramban MAUPUN Node — `Buffer` tak ada di
 * peramban, dan `btoa` tak ada di tipe `lib` paket ini. Enam belas baris di sini lebih murah
 * daripada satu polyfill atau satu `lib: dom` yang diam-diam membuka seluruh API peramban ke kode
 * yang juga berjalan di server.
 */
const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
function toBase64(bytes) {
    let out = '';
    for (let i = 0; i < bytes.length; i += 3) {
        const a = bytes[i] ?? 0;
        const b = bytes[i + 1] ?? 0;
        const c = bytes[i + 2] ?? 0;
        const n = (a << 16) | (b << 8) | c;
        out += B64[(n >> 18) & 63] ?? '';
        out += B64[(n >> 12) & 63] ?? '';
        out += i + 1 < bytes.length ? (B64[(n >> 6) & 63] ?? '') : '=';
        out += i + 2 < bytes.length ? (B64[n & 63] ?? '') : '=';
    }
    return out;
}
/** ⛔ Mengembalikan `undefined` untuk masukan yang tak terbaca — tak pernah melempar. */
function fromBase64(s) {
    const bersih = s.replace(/=+$/, '');
    if (/[^A-Za-z0-9+/]/.test(bersih))
        return undefined;
    const out = new Uint8Array(Math.floor((bersih.length * 6) / 8));
    let acc = 0;
    let bits = 0;
    let o = 0;
    for (const ch of bersih) {
        acc = (acc << 6) | B64.indexOf(ch);
        bits += 6;
        if (bits >= 8) {
            bits -= 8;
            out[o] = (acc >> bits) & 0xff;
            o += 1;
        }
    }
    return out;
}
export function encodeLogoRaster(r) {
    return `${String(r.width)}:${String(r.height)}:${toBase64(r.bits)}`;
}
/**
 * ⛔ Mengembalikan `undefined` untuk apa pun yang tak terbaca — **tak pernah melempar**.
 *
 * Nilainya datang dari kolom teks yang bisa diisi importir, SQL, atau versi lama. Renderer yang
 * melempar di tengah struk berarti pesanan yang sudah dibayar gagal dicetak; logo yang hilang cuma
 * struk tanpa logo (P8: jangan terbitkan dokumen bila masukannya tak ada — tapi logo bukan masukan
 * yang menentukan kebenaran angka).
 */
export function decodeLogoRaster(v) {
    if (v === null || v === undefined || v === '')
        return undefined;
    const [w, h, b64] = v.split(':');
    const width = Number(w);
    const height = Number(h);
    if (!Number.isInteger(width) || !Number.isInteger(height))
        return undefined;
    if (width <= 0 || height <= 0 || b64 === undefined)
        return undefined;
    const perRow = Math.ceil(width / 8);
    const bits = fromBase64(b64);
    /* ⚠️ Panjang WAJIB cocok: raster separuh terkirim menghasilkan gambar yang bergeser makin jauh
       tiap baris — dan yang melihatnya cuma tamu, sesudah ratusan struk tercetak. */
    if (bits === undefined || bits.length !== perRow * height)
        return undefined;
    return { width, height, bits };
}
/**
 * Apakah transport ini **menuntut `device-agent`** di mesin kasir? (K4)
 *
 * `net` bisa dicetak dari mana saja di LAN; `usb`/`bt` hanya dari mesin tempat printer menempel.
 */
export function requiresLocalAgent(t) {
    return t !== 'net';
}
/**
 * Apakah transport ini butuh **pairing/izin di tingkat OS** sebelum bisa dipakai?
 *
 * ⚠️ Dinyatakan eksplisit karena ia **melanggar semangat K6** dan tak boleh disembunyikan: printer
 * Bluetooth yang belum dipasangkan akan gagal dengan cara yang tampak seperti "printer rusak",
 * padahal masalahnya ada di setelan sistem operasi mesin kasir.
 */
export function requiresOsPairing(t) {
    return t === 'bt';
}
/**
 * **Merapikan MAC Bluetooth apa adanya jadi bentuk kanonik `AA:BB:CC:DD:EE:FF`.**
 *
 * ⛔ Lubang yang ditutupnya (2026-08-25): kotak isian MAC di portal menerima teks bebas, sementara
 * {@link isPrinterAddress} hanya menerima bentuk ber-titik-dua huruf besar. Orang menyalin MAC dari
 * Setelan Android (`7c-6c-f0-e2-24-45`), dari lembar self-test printer (`7C6CF0E22445`), atau dari
 * label yang memakai titik — semuanya tersimpan, dan **baru gagal saat mencetak**, dengan pesan
 * yang menunjuk profil, bukan huruf yang salah. Itu pola "setelan yang berbohong" (Guardrail #6).
 *
 * ⚠️ Ia **tidak** menebak MAC yang kurang huruf. Yang bukan 12 digit heksadesimal ⇒ `null`, karena
 * MAC yang ditambal sendiri menunjuk perangkat lain — dan di Bluetooth, perangkat lain itu bisa
 * saja menjawab.
 *
 * @returns bentuk kanonik, atau `null` bila bukan MAC.
 */
export function normalizeMac(raw) {
    if (typeof raw !== 'string')
        return null;
    const hex = raw.replace(/[\s:.-]/g, '').toUpperCase();
    if (!/^[0-9A-F]{12}$/.test(hex))
        return null;
    return (hex.match(/.{2}/g) ?? []).join(':');
}
/**
 * Guard nilai `receipt_font` dari DB. ⚠️ Yang tak dikenal jatuh ke bawaan di pemanggil, tak
 * melempar — satu kolom setelan yang aneh tak boleh menghentikan struk yang uangnya sudah diterima.
 */
export function isReceiptFont(v) {
    return typeof v === 'string' && RECEIPT_FONTS.includes(v);
}
/** Guard nilai `receipt_name_scale` dari DB. Sama alasannya dengan {@link isReceiptFont}. */
export function isReceiptScale(v) {
    return typeof v === 'string' && RECEIPT_SCALES.includes(v);
}
/** Guard {@link PrinterAddress} untuk nilai dari kawat/DB. Fail-closed. */
export function isPrinterAddress(v) {
    if (typeof v !== 'object' || v === null)
        return false;
    const o = v;
    switch (o['transport']) {
        case 'net':
            return typeof o['host'] === 'string' && o['host'] !== '' && Number.isInteger(o['port']);
        case 'usb':
            return typeof o['vendorId'] === 'string' && typeof o['productId'] === 'string';
        case 'bt':
            return typeof o['mac'] === 'string' && /^([0-9A-Fa-f]{2}:){5}[0-9A-Fa-f]{2}$/.test(o['mac']);
        default:
            return false;
    }
}
/** Bentuk alamat yang bisa dibaca manusia — untuk log & layar diagnosa, bukan untuk logika. */
export function describeAddress(a) {
    switch (a.transport) {
        case 'net':
            return `net ${a.host}:${String(a.port)}`;
        case 'usb':
            return `usb ${a.vendorId}:${a.productId}${a.serial === undefined ? '' : ` (${a.serial})`}`;
        case 'bt':
            return `bt ${a.mac}`;
    }
}
/**
 * Preset profil printer — **DATA**, bukan cabang kode (K6).
 *
 * Menambah dukungan model = menambah baris di sini. ⚠️ Nilai `codepage` & kemampuan diambil dari
 * lembar data Epson, dan **belum diverifikasi terhadap perangkat nyata** — verifikasinya adalah
 * spike yang tak bisa diselesaikan dengan berpikir (lihat `RUNBOOK.md`).
 */
export const PRINTER_PRESETS = Object.freeze({
    /** Epson TM-T82 — 80mm, dipakai Arena. Keluarga TM berbagi perintah ESC/POS yang sama. */
    'epson-tm-t82': {
        paperWidth: 80,
        codepage: 0, // PC437 — aman untuk huruf Indonesia tanpa diakritik
        hasCutter: true,
        hasDrawerKick: true,
        model: 'Epson TM-T82',
    },
    /** Epson TM-T88 — 80mm, seri dapur/volume tinggi. */
    'epson-tm-t88': {
        paperWidth: 80,
        codepage: 0,
        hasCutter: true,
        hasDrawerKick: true,
        model: 'Epson TM-T88',
    },
    /** Epson TM-m30 — 80mm, ringkas; varian Bluetooth umum untuk UMKM. */
    'epson-tm-m30': {
        paperWidth: 80,
        codepage: 0,
        hasCutter: true,
        hasDrawerKick: true,
        model: 'Epson TM-m30',
    },
    /** Generik 58mm — printer termal murah kelas UMKM; sering TANPA pemotong. */
    'generic-58': {
        paperWidth: 58,
        codepage: 0,
        hasCutter: false,
        hasDrawerKick: false,
        model: 'Generic 58mm',
    },
});
/** Membentuk profil lengkap dari preset + alamat. Melempar bila presetnya tak dikenal (P6). */
export function profilDariPreset(preset, address) {
    const p = PRINTER_PRESETS[preset];
    if (p === undefined) {
        throw new Error(`preset printer "${preset}" tak dikenal — tambahkan ke PRINTER_PRESETS, jangan beri default: ` +
            'profil yang salah menghasilkan struk yang terbaca benar di layar tapi kacau di kertas.');
    }
    return { ...p, address };
}
//# sourceMappingURL=printer.js.map